import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Form, message, Typography, Divider } from 'antd'

import { useDataApi } from '../../../api/useDataApi'
import { getHeaders } from '../../../api/react-query/api'
import { NameFormItem } from '../../../components/Common'
import MiddleNameFormItem from '../../../components/Common/FormItems/MiddleNameFormItem'
import PassportSeriesFormItem from '../../../components/Common/FormItems/Passport/PassportSeriesFormItem'
import PassportNumberFormItem from '../../../components/Common/FormItems/Passport/PassportNumberFormItem'
import PassportIssuerNameFormItem from '../../../components/Common/FormItems/Passport/PassportIssuerNameFormItem'
import PassportIssuerCodeFormItem from '../../../components/Common/FormItems/Passport/PassportIssuerCodeFormItem'
import PassportIssueDateFormItem from '../../../components/Common/FormItems/Passport/PassportIssueDateFormItem'
import { toServerUtcDate, validateName } from '../../../helper'
import { profileScreen } from '../../../translates'

const { Text, Title } = Typography

const buildOfferPassportProfilePatch = (personalData, offerErrorLabels) => {
  const inputs = profileScreen.input
  const passport = personalData?.passport || {}
  const patch = { personalData: {} }
  const has = (label) => offerErrorLabels.includes(label)

  if (has(inputs.surname.label)) {
    patch.personalData.surname = personalData?.surname?.trim()
  }
  if (has(inputs.firstName.label)) {
    patch.personalData.firstName = personalData?.firstName?.trim()
  }
  if (has(inputs.middleName.label)) {
    patch.personalData.withoutMiddleName = !!personalData?.withoutMiddleName
    patch.personalData.middleName = personalData?.withoutMiddleName ? '' : personalData?.middleName?.trim()
  }

  const passportPatch = {}
  if (has(inputs.passportseries.label)) passportPatch[inputs.passportseries.name] = passport[inputs.passportseries.name]
  if (has(inputs.passportnumber.label)) passportPatch[inputs.passportnumber.name] = passport[inputs.passportnumber.name]
  if (has(inputs.passportissuerName.label)) passportPatch[inputs.passportissuerName.name] = passport[inputs.passportissuerName.name]
  if (has(inputs.passportissuerCode.label)) passportPatch[inputs.passportissuerCode.name] = passport[inputs.passportissuerCode.name]
  if (has(inputs.passportissueDate.label)) {
    passportPatch[inputs.passportissueDate.name] = toServerUtcDate(passport[inputs.passportissueDate.name])
  }
  if (Object.keys(passportPatch).length) {
    patch.personalData.passport = passportPatch
  }

  return patch
}

const getBlockValidateFieldNames = (offerErrors) => {
  const inputs = profileScreen.input
  const names = []
  if (offerErrors.includes(inputs.surname.label)) names.push(['personalData', 'surname'])
  if (offerErrors.includes(inputs.firstName.label)) names.push(['personalData', 'firstName'])
  if (offerErrors.includes(inputs.middleName.label)) {
    names.push(['personalData', 'middleName'], ['personalData', 'withoutMiddleName'])
  }
  if (offerErrors.includes(inputs.passportseries.label)) names.push(['personalData', 'passport', inputs.passportseries.name])
  if (offerErrors.includes(inputs.passportnumber.label)) names.push(['personalData', 'passport', inputs.passportnumber.name])
  if (offerErrors.includes(inputs.passportissuerName.label)) names.push(['personalData', 'passport', inputs.passportissuerName.name])
  if (offerErrors.includes(inputs.passportissuerCode.label)) names.push(['personalData', 'passport', inputs.passportissuerCode.name])
  if (offerErrors.includes(inputs.passportissueDate.label)) names.push(['personalData', 'passport', inputs.passportissueDate.name])
  return names
}

/** Блок только с незаполненными полями ФИО/паспорта по offerErrors (экран ЛМК), без подстановки из профиля. */
const OfferPassportData = ({
  offerErrors,
  setPartnerProfile,
  partnerProfileId,
  onAfterProfilePatchSuccess
}) => {
  const [passportBlockForm] = Form.useForm()
  const [middleNameTrigger, setMiddleNameTrigger] = useState(false)
  const [{ data: profileUpdated, isLoading: isProfilePatchLoading }, doFetchProfileUpdate] = useDataApi({})

  const inputs = profileScreen.input
  const blockValidateFieldNames = useMemo(() => getBlockValidateFieldNames(offerErrors), [offerErrors])
  const offerErrKey = offerErrors.join('|')
  const initKeyRef = useRef('')

  useEffect(() => {
    if (offerErrors.length === 0) {
      initKeyRef.current = ''
      return
    }
    if (initKeyRef.current === offerErrKey) return
    initKeyRef.current = offerErrKey
    passportBlockForm.resetFields()
    setMiddleNameTrigger(t => !t)
  }, [offerErrKey, offerErrors.length, passportBlockForm])

  useEffect(() => {
    if (profileUpdated?.isSuccess && profileUpdated.profile) {
      message.success(profileScreen.messages.profileSaved)
      setPartnerProfile({
        ...profileUpdated,
        ...(partnerProfileId ? { id: partnerProfileId } : {})
      })
      passportBlockForm.resetFields()
      initKeyRef.current = ''
      onAfterProfilePatchSuccess?.()
    } else if (profileUpdated && !profileUpdated.isSuccess && profileUpdated.errorMessage) {
      message.error(profileUpdated.errorMessage)
    }
  }, [
    profileUpdated,
    partnerProfileId,
    setPartnerProfile,
    passportBlockForm,
    onAfterProfilePatchSuccess
  ])

  const handleSubmit = () => {
    passportBlockForm.validateFields(blockValidateFieldNames).then(() => {
      const values = passportBlockForm.getFieldsValue()
      const info = buildOfferPassportProfilePatch(values.personalData, offerErrors)
      doFetchProfileUpdate('partner/profile/kafkaflow', { // /api/partner/profile/kafkaflow
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({ info })
      })
    }).catch(() => { })
  }

  if (offerErrors.length === 0) {
    return null
  }

  return (
    <div>
      <Title level={4} className='mt-0'>
        Заполните паспортные данные перед заполнением анкеты ЛМК
      </Title>
      <div className='mb-3'>
        <Text type='secondary' className='pre-line'>
          Не заполнено: {offerErrors.join(', ')}
        </Text>
      </div>
      <Form form={passportBlockForm} layout='vertical'>
        {offerErrors.includes(inputs.surname.label) && (
          <NameFormItem
            name={['personalData', 'surname']}
            placeholder={inputs.surname.placeholder}
            rules={[{ required: true, message: inputs.surname.message, validator: validateName }]}
            label={inputs.surname.label}
          />
        )}
        {offerErrors.includes(inputs.firstName.label) && (
          <NameFormItem
            name={['personalData', 'firstName']}
            placeholder={inputs.firstName.placeholder}
            rules={[{ required: true, message: inputs.firstName.message, validator: validateName }]}
            label={inputs.firstName.label}
          />
        )}
        {offerErrors.includes(inputs.middleName.label) && (
          <MiddleNameFormItem form={passportBlockForm} trigger={middleNameTrigger} />
        )}
        {offerErrors.includes(inputs.passportseries.label) && <PassportSeriesFormItem classNameInput='noScrollNumber' />}
        {offerErrors.includes(inputs.passportnumber.label) && <PassportNumberFormItem classNameInput='noScrollNumber' />}
        {offerErrors.includes(inputs.passportissuerName.label) && <PassportIssuerNameFormItem />}
        {offerErrors.includes(inputs.passportissuerCode.label) && <PassportIssuerCodeFormItem />}
        {offerErrors.includes(inputs.passportissueDate.label) && <PassportIssueDateFormItem />}
        <Button htmlType='submit' type='primary' size='large' className='mt-3' style={{ marginBottom: 20 }} block loading={isProfilePatchLoading} onClick={handleSubmit}>
          Сохранить
        </Button>
      </Form>
      <Divider className='small' />
    </div>
  )
}

export default OfferPassportData
